import {applySpec, curryRight, defaultTo, get} from '@logistics-frontend/utils'
import {LngLat} from '@yandex-int/ymaps3-react/deprecated'
import {MapContext} from '@yandex-int/ymaps3-react/deprecated/MapComponent'
import {useCallback, useContext} from 'react'

import {useTransition} from '../../v2'
import {getGeolocationFromBrowser} from '../utils'
import {fetchGeolocationIfAllowed} from './GeolocationButton.api'
import {GEOLOCATION_KEY} from './GeolocationButton.consts'
import {GeolocationButtonProps, GeolocationEventSource} from './GeolocationButton.types'

const toLngLat = applySpec<LngLat>([get('long'), get('lat')])

type GeolocationState = {
  location: LngLat
  error: boolean
  isLoading: boolean
  updateLocation: (event: GeolocationEventSource) => void
  getGeolocationAccess: VoidFunction
}

export const useGeolocationState = ({
  initialLocation,
  onGeolocatePosition,
  withoutAnimation,
}: GeolocationButtonProps): GeolocationState => {
  const mapContext = useContext(MapContext)
  const fallbackLocation = initialLocation || toLngLat(mapContext?.meta.geolocation)
  const [{payload: location = fallbackLocation, error = false, isPending: isLoading}, startTransition] = useTransition<
    LngLat,
    boolean
  >(() => Promise.resolve(fallbackLocation), {key: GEOLOCATION_KEY})

  const geolocatePositionHandler = useCallback(
    (newLocation: LngLat, event: GeolocationEventSource) => {
      if (onGeolocatePosition) {
        onGeolocatePosition(newLocation, event)
      }

      if (mapContext && !withoutAnimation) {
        const zoom = mapContext.map.zoom
        mapContext.map.animateCenterAndZoom(newLocation, zoom, 450)
      }
    },
    [mapContext, withoutAnimation, onGeolocatePosition],
  )

  const updateLocation = useCallback(
    (event: GeolocationEventSource) => {
      if (!mapContext) {
        return
      }

      return startTransition(getGeolocationFromBrowser, {
        onCommit: curryRight(geolocatePositionHandler)(event),
        onError: () => geolocatePositionHandler(fallbackLocation, event),
      })
    },
    [mapContext, fallbackLocation, geolocatePositionHandler],
  )

  const getGeolocationAccess = useCallback(() => {
    startTransition(() => fetchGeolocationIfAllowed().then(defaultTo(fallbackLocation)), {
      onCommit: newLocation => geolocatePositionHandler(newLocation, 'mount'),
      onError: () => geolocatePositionHandler(fallbackLocation, 'mount'),
    })
  }, [fallbackLocation, geolocatePositionHandler])

  return {isLoading, location, error, updateLocation, getGeolocationAccess}
}
